import { memo, useRef, useState } from 'react';
import { NodeResizer, type NodeProps } from '@xyflow/react';
import { PaletteIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import { cn } from 'src/lib/utils';
import { Input } from 'src/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import {
  STEP_LAYOUT_GROUP_HEADER_PADDING,
  STEP_LAYOUT_GROUP_MIN_HEIGHT,
  STEP_LAYOUT_GROUP_MIN_WIDTH,
  type StepLayoutGroupNode as StepLayoutGroupNodeType,
} from 'src/lib/workflows/stepLayoutGroups';
import {
  getStepLayoutGroupPalette,
  STEP_LAYOUT_GROUP_COLORS,
  stepLayoutGroupPalette,
} from 'src/lib/workflows/stepLayoutGroupPalette';

/**
 * The visual container behind a region of step nodes. Carries a label and a
 * colour and nothing else — it has no sockets, emits nothing, and never
 * participates in execution or validation.
 */
// Kept deliberately, unlike the `useMemo`/`useCallback` this file used to carry.
// React Compiler can't cover this one: `vite.config.ts` excludes node_modules,
// so React Flow's own `NodeWrapper` — which renders this component on every
// store tick — is uncompiled and has nothing to bail out on. `memo` is the only
// thing standing between a node drag and a re-render of every box on the canvas.
// The sibling `WorkflowFrameNode` keeps it for the same reason.
// react-doctor-disable-next-line react-doctor/react-compiler-no-manual-memoization
export const StepLayoutGroupNode = memo(function StepLayoutGroupNode({
  id,
  data,
  selected,
}: NodeProps<StepLayoutGroupNodeType>) {
  const gt = useGT();
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [draftLabel, setDraftLabel] = useState(data.label);
  const inputRef = useRef<HTMLInputElement>(null);

  const onRenameStepLayoutGroup = data.onRenameStepLayoutGroup;
  const onRecolorStepLayoutGroup = data.onRecolorStepLayoutGroup;
  // No injected callbacks means no editor behind this render (a run page, a
  // version preview) — treat that as read-only rather than showing affordances
  // that would do nothing.
  const isReadOnly =
    data.isReadOnly === true ||
    (!onRenameStepLayoutGroup && !onRecolorStepLayoutGroup);

  // `derivedColor` still tracks the graph live — it's recomputed by the editors'
  // decoration pass, which already runs on every node change — but deriving it
  // there costs one pass over the node list instead of one per group per store
  // update, which was a measurable drag stutter on a large canvas.
  const activeColor = data.color ?? data.derivedColor ?? null;
  const palette = getStepLayoutGroupPalette(activeColor);

  const commitLabel = () => {
    setIsEditingLabel(false);
    const next = draftLabel.trim();
    if (next.length === 0 || next === data.label) {
      setDraftLabel(data.label);
      return;
    }
    onRenameStepLayoutGroup?.(id, next);
  };

  const startEditing = () => {
    if (isReadOnly || !onRenameStepLayoutGroup) return;
    setDraftLabel(data.label);
    setIsEditingLabel(true);
    // Focus after the input mounts.
    requestAnimationFrame(() => inputRef.current?.select());
  };

  const colorOptions = STEP_LAYOUT_GROUP_COLORS.map((color) => ({
    color,
    name: stepLayoutGroupPalette[color].name,
    swatch: stepLayoutGroupPalette[color].swatch,
  }));

  return (
    <>
      <div
        className={cn(
          'relative h-full w-full rounded-xl border-2 border-dashed transition-colors',
          palette.surface,
          selected ? palette.selectedBorder : palette.border,
        )}
      >
        {/* The title bar: the box's whole interactive surface, and the only part
            that takes pointer events. The interior below it must stay
            transparent — a backdrop spanning a large region would otherwise
            intercept every canvas gesture starting inside it (shift-drag lasso,
            panning, pane right-click). So the drag target is this full-width
            band rather than the label alone, which was needlessly small. */}
        <div
          className={cn(
            'step-layout-group-drag-handle absolute inset-x-0 top-0',
            // Read-only graphs get no band at all, so it can't intercept canvas
            // gestures where there's nothing to drag.
            !isReadOnly &&
              'pointer-events-auto cursor-grab active:cursor-grabbing',
          )}
          style={{ height: STEP_LAYOUT_GROUP_HEADER_PADDING }}
          onDoubleClick={startEditing}
        />

        {/* Straddles the top border, like the workflow frame's pill. Sits above
            the band so its own controls stay clickable. No `nodrag` on it — that
            would apply to descendants and defeat the `dragHandle` selector. */}
        <div className="pointer-events-auto absolute left-4 top-0 flex max-w-[calc(100%-2rem)] -translate-y-1/2 items-center gap-1">
          {isEditingLabel ? (
            <Input
              ref={inputRef}
              value={draftLabel}
              onChange={(event) => setDraftLabel(event.target.value)}
              onBlur={commitLabel}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  commitLabel();
                } else if (event.key === 'Escape') {
                  event.preventDefault();
                  setDraftLabel(data.label);
                  setIsEditingLabel(false);
                }
              }}
              // Stops React Flow from treating typing in here as canvas
              // shortcuts (Backspace would delete the group).
              onKeyDownCapture={(event) => event.stopPropagation()}
              autoFocus
              className="nodrag nopan h-7 w-52 text-xs"
              aria-label={gt('Layout group label', {
                context:
                  'accessible label for the step layout group rename input',
              })}
            />
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onDoubleClick={startEditing}
                  // Rename was pointer-only: a keyboard user could tab here and
                  // Enter/Space did nothing.
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      startEditing();
                    }
                  }}
                  // No dead focus stop in a read-only graph.
                  tabIndex={isReadOnly ? -1 : 0}
                  className={cn(
                    'step-layout-group-drag-handle max-w-[320px] truncate rounded-md border px-2.5 py-1 text-xs font-semibold shadow-sm',
                    palette.label,
                    !isReadOnly && 'cursor-grab active:cursor-grabbing',
                  )}
                >
                  {data.label}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {isReadOnly ? (
                  <T context="tooltip on a step layout group label in a read-only workflow graph">
                    Layout group
                  </T>
                ) : (
                  <T context="tooltip on a step layout group label explaining how to move and rename it">
                    Drag to move the group and its steps · double-click to
                    rename
                  </T>
                )}
              </TooltipContent>
            </Tooltip>
          )}

          {!isReadOnly && onRecolorStepLayoutGroup && !isEditingLabel && (
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="nodrag nopan rounded-md border border-border bg-background/90 p-1 text-muted-foreground shadow-sm hover:text-foreground"
                  aria-label={gt('Change layout group color', {
                    context:
                      'accessible label for the step layout group color picker button',
                  })}
                >
                  <PaletteIcon className="h-3.5 w-3.5" />
                </button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-56 p-2">
                <div className="mb-2 text-xs font-medium text-muted-foreground">
                  <T context="heading of the step layout group color picker">
                    Layout group color
                  </T>
                </div>
                <div className="grid grid-cols-7 gap-1.5">
                  {colorOptions.map((option) => (
                    <button
                      key={option.color}
                      type="button"
                      title={option.name}
                      aria-label={option.name}
                      aria-pressed={data.color === option.color}
                      onClick={() => onRecolorStepLayoutGroup(id, option.color)}
                      className={cn(
                        'h-6 w-6 rounded-md border-2',
                        option.swatch,
                        data.color === option.color
                          ? 'border-foreground'
                          : 'border-transparent hover:border-border',
                      )}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => onRecolorStepLayoutGroup(id, null)}
                  className={cn(
                    'mt-2 w-full rounded-md border px-2 py-1 text-left text-xs',
                    data.color === null
                      ? 'border-foreground text-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  <T context="option in the step layout group colour picker that derives the colour from the grouped steps">
                    Match steps
                  </T>
                </button>
              </PopoverContent>
            </Popover>
          )}
        </div>
      </div>
      {/* After the body on purpose: the resize lines and handles sit on the same
          edges as the title-bar band above, and whichever paints last wins the
          hit test. Resizing a selected box has to beat dragging it. */}
      {!isReadOnly && (
        <NodeResizer
          minWidth={STEP_LAYOUT_GROUP_MIN_WIDTH}
          minHeight={STEP_LAYOUT_GROUP_MIN_HEIGHT}
          isVisible={selected}
          // The node wrapper is `pointer-events: none` so the box doesn't eat
          // canvas gestures; the resize affordances have to opt back in.
          lineClassName="pointer-events-auto border-transparent"
          handleClassName="pointer-events-auto h-2 w-2 rounded-sm border border-background bg-foreground/60"
          // The resizer hands back the final rect, which is more trustworthy
          // than the node's measured size at this instant. React Flow already
          // compensates member positions when the top-left edge moves, so the
          // contents stay put and only membership needs re-deriving.
          onResizeEnd={(_event, params) =>
            data.onResizeStepLayoutGroupEnd?.(id, {
              x: params.x,
              y: params.y,
              width: params.width,
              height: params.height,
            })
          }
        />
      )}
    </>
  );
});
